import { getQuestionModel } from '..'
import CompositeTimeline from './CompositeTimeline'

import { IQuestionModel, IQuestionModelConstructor, QuestionType } from '../types'
import { CompositeQuestionModelTypes, CompositeSubmitResponse, ICompositeQuestionModel } from './types'

const CompositeQuestionModel: IQuestionModelConstructor<CompositeQuestionModelTypes> = (questionSchema, action) => {
  const currentQuestionIndexRef = { current: 0 }

  const questionModels: IQuestionModel[] =
    questionSchema.entities?.map(entryQuestionSchema =>
      getQuestionModel(entryQuestionSchema)(entryQuestionSchema, action)
    ) || []

  const timeline = CompositeTimeline({
    id: questionSchema.id,
    questionModels,
    currentQuestionIndexRef
  })

  const preloadImages: string[] = questionModels.reduce((preloadImages, currentQuestionModel) => {
    const currentQuestionPreloadImages = currentQuestionModel.preloadImages
    preloadImages.push(...currentQuestionPreloadImages)
    return preloadImages
  }, [] as string[])

  const model: ICompositeQuestionModel = {
    get id() {
      return questionSchema.id
    },
    get type() {
      return QuestionType.COMPOSITE
    },
    get schema() {
      return questionSchema
    },
    get action() {
      return action
    },
    get checkResponse() {
      return questionModels.map(currentQuestionModel => currentQuestionModel.checkResponse)
    },
    get response() {
      return questionModels.map(currentQuestionModel => currentQuestionModel.response)
    },
    set response(value) {
      questionModels.forEach((currentQuestionModel, index) => {
        if (value?.[index]) {
          currentQuestionModel.response = value[index]
        }
      })
    },
    get responseValidation() {
      const currentQuestionModel = questionModels[currentQuestionIndexRef.current]
      return currentQuestionModel ? currentQuestionModel.responseValidation : { valid: false }
    },
    get submitResponse() {
      return questionModels.reduce((submitResponse, currentQuestionModel) => {
        const currentQuestionSubmitResponse = currentQuestionModel.submitResponse
        if (Array.isArray(currentQuestionSubmitResponse)) {
          submitResponse.push(...(currentQuestionSubmitResponse as unknown[]))
        } else {
          submitResponse.push(currentQuestionSubmitResponse)
        }
        return submitResponse
      }, [] as CompositeSubmitResponse)
    },
    get frame() {
      return currentQuestionIndexRef.current
    },
    set frame(questionIndex) {
      currentQuestionIndexRef.current = questionIndex
    },
    get currentQuestionModel() {
      return questionModels[currentQuestionIndexRef.current]
    },
    nextFrame() {
      if (currentQuestionIndexRef.current < questionModels.length - 1) {
        currentQuestionIndexRef.current++
        return true
      } else {
        return false
      }
    },
    prevFrame() {
      if (0 < currentQuestionIndexRef.current) {
        currentQuestionIndexRef.current--
        return true
      } else {
        return false
      }
    },
    get timeline() {
      return timeline
    },
    get preloadImages() {
      return preloadImages
    },
    get questionTime() {
      return questionSchema?.time || 0
    },
    get screenNumber() {
      let screenNumber = 0
      for (let i = 0; i <= currentQuestionIndexRef.current; i++) {
        screenNumber += questionModels[i]?.screenNumber
      }
      return screenNumber
    },
    set screenNumber(screenNumber) {
      // don't set screen number for composite question, it's set in each entry question model individually
    },
    nextScreenNumber() {
      // don't set screen number for composite question, it's set in each entry question model individually
      return model.screenNumber
    },
    prevScreenNumber() {
      // don't set screen number for composite question, it's set in each entry question model individually
      return model.screenNumber
    },
    get navigationFrame() {
      return questionModels[currentQuestionIndexRef.current]?.navigationFrame || null
    },
    set navigationFrame(navigationFrame) {
      const currentQuestionModel = questionModels[currentQuestionIndexRef.current]
      if (currentQuestionModel) {
        currentQuestionModel.navigationFrame = navigationFrame
      }
    }
  }
  return model
}

export default CompositeQuestionModel
